import { json, type LoaderFunctionArgs } from '@remix-run/node';
import { type ShouldRevalidateFunction } from '@remix-run/react';
import { getTopNavigation } from '@modules/navigation';
import { getCombinedNotifications } from '~/modules/notifications/api/get-combined-notifications.server';
import { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import { getShoppingCartSummary } from '@modules/ecommerce/api/get-shopping-cart-summary.server';
import {
    hasFeature,
    isUsProductLaunchEnabled,
    shouldNotRevalidateRoot,
} from '@modules/root';
import { getPermissions } from '~/modules/user';
import { PermissionEnum } from 'generated/graphql';
import { getSession } from '~/modules/root/util/session.server';
import AppPage from '@modules/root/page/AppPage';
import { toggleFeatures } from '~/modules/root/util/toggle-features';
import { getBottomContent } from '@modules/root/api/get-bottom-content.server';
import { getFilterDrawerCookieValue } from '~/modules/search/hook/use-filter-drawer-preference';
import { getMoodBoardPageIds } from '@modules-rocco/mood-board/api/get-mood-board-page-ids.server';
import { getMonthlyUsers } from '~/modules/root/api/get-monthly-users.server';
import { getRecentMagazines } from '~/modules/magazine/api/get-recent-magazines.server';
import { getFooterContent } from '~/modules-rocco/footer/api/get-footer-content.server';

/**
 * global catch and error boundary for all pages under __public
 * Child routes can still catch their own but if they don't we'll end up here
 * root.tsx will still render top nav etc
 */
export { ErrorBoundary, links } from '@modules/root/page/AppPage';

export const loader = async (args: LoaderFunctionArgs) => {
    const { context, request } = args;
    const { appDisplayMode, device, user, featureFlags, webSocketUrl } =
        context;
    const response = toggleFeatures(request);
    if (response) throw response;

    const footerRedesignEnabled =
        hasFeature(featureFlags, 'footer_redesign_dec_2025') &&
        appDisplayMode === 'desktop';

    const url = new URL(request.url);

    if (
        url.pathname.startsWith('/search') &&
        appDisplayMode !== 'desktop' &&
        !device.isBot
    ) {
        const [
            notifications,
            abTestConfig,
            session,
            moodBoardPageIds,
            monthlyUsers,
        ] = await Promise.all([
            getCombinedNotifications(context),
            context.getABTests(),
            getSession(request),
            getMoodBoardPageIds(context),
            getMonthlyUsers(context),
        ]);

        const bookConsultationEnabled = false;

        const ecommerceActiveEnabled = hasFeature(
            featureFlags,
            'ecommerce_active',
        );
        const autoSubmitSuccess = session.getAutoSubmitSuccess();

        const availableForWorkEnabled = hasFeature(
            featureFlags,
            'available_for_work',
        );
        return json(
            {
                user,
                featureFlags,
                topNavigation: null,
                notifications,
                localisation: null,
                shoppingCartSummary: null,
                webSocketUrl,
                ecommerceActiveEnabled,
                bookConsultationEnabled,
                canApproveContent: getPermissions(
                    user,
                    PermissionEnum.BmContentApprove,
                ),
                canCreateProfessional: getPermissions(
                    user,
                    PermissionEnum.BmProfessionalCreate,
                ),
                toastMessage: session.getToastMessage(),
                autoSubmitSuccess,
                abTestConfig,
                showReseneSponsor: false,
                availableForWorkEnabled,
                professionalBookingLink: null,
                moodBoardPageIds,
                bottomContent: null,
                filterDrawerCookieValue: getFilterDrawerCookieValue(request),
                monthlyUsers,
                footerRedesignEnabled: false,
                recentMagazines: [],
                footerVideoContent: null,
            },
            {
                headers: await session.getHeaders({
                    'Cache-Control': 'no-cache',
                }),
            },
        );
    }

    // The shopping cart only exists in markets with ecommerce/products. Skipping
    // the fetch for non-product markets (e.g. US) avoids a needless GraphQL
    // request and the server-side "Invalid tax provider" error it would
    // otherwise trigger while resolving cart tax totals.
    const shoppingCartAvailable = isUsProductLaunchEnabled(
        context.localisation.countryConfig,
        featureFlags,
    );

    const [
        topNavigation,
        localisation,
        notifications,
        shoppingCartSummary,
        abTestConfig,
        session,
        bottomContent,
        moodBoardPageIds,
        monthlyUsers,
        recentMagazinesResult,
        footerVideoContent,
    ] = await Promise.all([
        getTopNavigation(context),
        getPageLocalisation(args),
        getCombinedNotifications(context),
        shoppingCartAvailable
            ? getShoppingCartSummary(context)
            : Promise.resolve(null),
        context.getABTests(),
        getSession(request),
        getBottomContent(context),
        getMoodBoardPageIds(context),
        getMonthlyUsers(context),
        footerRedesignEnabled
            ? getRecentMagazines({ ...context, limit: 3 })
            : Promise.resolve({ magazines: [] }),
        footerRedesignEnabled
            ? getFooterContent(context)
            : Promise.resolve(null),
    ]);

    // Currently not being used.
    // const tabletEnabled = hasFeature(user.FeatureFlags, 'enable_tablet');

    const bookConsultationEnabled = false;

    const ecommerceActiveEnabled = hasFeature(featureFlags, 'ecommerce_active');

    const autoSubmitSuccess = session.getAutoSubmitSuccess();

    const availableForWorkEnabled = hasFeature(
        featureFlags,
        'available_for_work',
    );

    return json(
        {
            user,
            featureFlags,
            topNavigation,
            localisation,
            notifications,
            shoppingCartSummary,
            webSocketUrl,
            ecommerceActiveEnabled,
            bookConsultationEnabled,
            canApproveContent: getPermissions(
                user,
                PermissionEnum.BmContentApprove,
            ),
            canCreateProfessional: getPermissions(
                user,
                PermissionEnum.BmProfessionalCreate,
            ),
            toastMessage: session.getToastMessage(),
            autoSubmitSuccess,
            abTestConfig,
            showReseneSponsor: false,
            availableForWorkEnabled,
            professionalBookingLink: null,
            moodBoardPageIds,
            bottomContent,
            filterDrawerCookieValue: getFilterDrawerCookieValue(request),
            monthlyUsers,
            footerRedesignEnabled,
            recentMagazines: recentMagazinesResult.magazines,
            footerVideoContent,
        },
        {
            headers: await session.getHeaders({
                // Make sure user data is not cached.
                'Cache-Control': 'no-cache',
            }),
        },
    );
};

export const shouldRevalidate: ShouldRevalidateFunction = ({
    currentUrl,
    defaultShouldRevalidate,
    formAction,
    formMethod,
    nextUrl,
}) => {
    if (!formMethod) {
        // Don't revalidate on search page to improve speed.
        if (nextUrl.pathname.startsWith('/search')) {
            return false;
        }
        // Don't revalidate if only search params change.
        if (
            currentUrl.pathname === nextUrl.pathname &&
            currentUrl.search !== nextUrl.search
        ) {
            return false;
        }
        // Require revalidation on login modal login.
    }
    if (formMethod === 'GET') {
        // Do not revalidate on GET submissions.
        return false;
    }
    if (shouldNotRevalidateRoot(formAction)) {
        return false;
    }
    return defaultShouldRevalidate;
};

const App = () => <AppPage />;

export default App;
